import { useState } from 'react';

import loadable from '@loadable/component';
import { BpkAccordion } from '@skyscanner-internal/backpack-web/bpk-component-accordion';
import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import { withRtlSupport } from '@skyscanner-internal/backpack-web/bpk-component-icon';
import ChevronDownIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-down';
import ChevronUpIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-up';
import BpkSectionHeader from '@skyscanner-internal/backpack-web/bpk-component-section-header';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import {
  ACTION_TYPE,
  COMPONENT_NAME,
} from '@skyscanner-internal/landing-pages-shared-constants/constants';

import logMiniEventHelper from '../../../mini-event/logMiniEventHelper';
import withErrorBoundary from '../../ErrorBoundary';
import { useMBDContext } from '../../common/MBD/useMBDContext';
import Faq from '../FAQ';

import type { FaqsProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/FaqsProps';

import STYLES from './FAQs.module.scss';

const IntersectionObserverWrapper = loadable(
  () => import('../../IntersectionObserverWrapper/IntersectionObserverWrapper'),
);

const AlignedChevronDown = withRtlSupport(ChevronDownIcon);
const AlignedChevronUp = withRtlSupport(ChevronUpIcon);

const TWO_COLUMN_SEE_MORE_THRESHOLD = 10;

const FAQs = ({
  faqs,
  layout = 'default',
  ldJsonData,
  postcardImageData,
  showLessLabel,
  showMoreLabel,
  subtitle,
  title,
  trackingContext,
}: FaqsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!faqs.length) {
    return null;
  }

  const faqItems = faqs.map((faqItem, index) => (
    <Faq
      {...faqItem}
      {...trackingContext}
      questionIndex={index}
      key={faqItem.id ?? `qa_${index}`}
    />
  ));

  const ldJsonScript = ldJsonData && (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: ldJsonData }}
    />
  );

  if (layout === 'twoColumn') {
    const hasMore =
      faqs.length > TWO_COLUMN_SEE_MORE_THRESHOLD &&
      !!showMoreLabel &&
      !!showLessLabel;
    const visibleFaqItems =
      hasMore && !isExpanded
        ? faqItems.slice(0, TWO_COLUMN_SEE_MORE_THRESHOLD)
        : faqItems;
    const ctaAction = isExpanded ? 'less' : 'more';

    return (
      <div
        className={`${STYLES.FAQs} ${STYLES['FAQs--twoColumn']}`}
        id="faqs"
        data-tracking-element-id={rawComponentName}
        data-tracking-common-params={buildMBDParam(rawComponentName)}
      >
        <div className={STYLES.FAQs__header}>
          <BpkSectionHeader title={title} description={subtitle} />
        </div>
        {postcardImageData && (
          <div className={STYLES['FAQs__twoColumn-image']}>
            <img
              id="postcardImage"
              alt={postcardImageData.name}
              src={postcardImageData.imageUrl}
            />
          </div>
        )}
        <BpkAccordion className={STYLES['FAQs__twoColumn-grid']}>
          {visibleFaqItems}
        </BpkAccordion>
        {hasMore && (
          <div className={STYLES.FAQs__expanded}>
            <BpkButton
              type={BUTTON_TYPES.link}
              implicit
              data-tracking-element-id={`${rawComponentName}_cta_${ctaAction}`}
              data-tracking-common-params={buildMBDParam(
                `${rawComponentName}_cta`,
                { cta_action: ctaAction },
              )}
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <BpkText
                tagName="span"
                textStyle={TEXT_STYLES.label2}
                color={TEXT_COLORS.textPrimary}
              >
                {isExpanded ? showLessLabel : showMoreLabel}
              </BpkText>
              {isExpanded ? <AlignedChevronUp /> : <AlignedChevronDown />}
            </BpkButton>
          </div>
        )}
        {ldJsonScript}
      </div>
    );
  }

  return (
    <>
      <div
        className={STYLES.FAQs}
        id="faqs"
        data-tracking-element-id={rawComponentName}
        data-tracking-common-params={buildMBDParam(rawComponentName)}
      >
        <div className={STYLES.FAQs__header}>
          <BpkSectionHeader title={title} description={subtitle} />
        </div>
        <BpkAccordion className={STYLES.FAQs__questions}>
          {faqItems}
        </BpkAccordion>
        {ldJsonScript}
      </div>
      {postcardImageData ? (
        <div className={STYLES.FAQs__image}>
          <img
            id="postcardImage"
            alt={postcardImageData.name}
            src={postcardImageData.imageUrl}
          />
        </div>
      ) : null}
    </>
  );
};

const FAQsWrapper = (props: FaqsProps) => (
  <IntersectionObserverWrapper
    onElementSeen={() =>
      logMiniEventHelper({
        action_type: ACTION_TYPE.COMPONENT_VISIBLE,
        component_name: COMPONENT_NAME.FAQS,
      })
    }
  >
    <FAQs {...props} />
  </IntersectionObserverWrapper>
);
const FAQsWithErrorBoundary = withErrorBoundary(FAQsWrapper, 'faqs-component');
export default FAQsWithErrorBoundary;
